import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Phone, Mail, MessageCircle, Send } from 'lucide-react';

const Contact = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent('Demande de réservation - HB Transfert');
    const body = encodeURIComponent(
      `Nom: ${formData.name}\nEmail: ${formData.email}\nTéléphone: ${formData.phone}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:contact@hbtransfert.com?subject=${subject}&body=${body}`;
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className={`relative py-24 md:py-32 overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0A0A0A]' : 'bg-[#FAF7F0]'
      }`}
    >
      {/* Subtle radial glow */}
      <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full blur-[150px] pointer-events-none ${
        isDark ? 'bg-[#D4AF37]/5' : 'bg-[#D4AF37]/10'
      }`} />

      {/* Grain texture */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Title */}
        <div className="text-center mb-16 md:mb-20">
          <h2 
            data-testid="contact-title"
            className={`font-playfair text-3xl sm:text-4xl lg:text-5xl mb-6 ${
              isDark ? 'text-white' : 'text-[#1A1A1A]'
            }`}
          >
            {t('contact.title')}
          </h2>
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className={`w-12 h-px ${isDark ? 'bg-[#D4AF37]/50' : 'bg-[#D4AF37]/70'}`} />
            <div className="w-2 h-2 rotate-45 bg-[#D4AF37]" />
            <div className={`w-12 h-px ${isDark ? 'bg-[#D4AF37]/50' : 'bg-[#D4AF37]/70'}`} />
          </div>
          <p className={`max-w-xl mx-auto text-base md:text-lg ${
            isDark ? 'text-[#F8F5EE]/70' : 'text-[#1A1A1A]/70'
          }`}>
            {t('contact.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Buttons */}
          <div className="flex flex-col gap-5">
            {/* Call Button */}
            <a
              href="tel:+33619362520"
              data-testid="contact-call-btn"
              className={`group flex items-center gap-4 p-5 rounded-sm border-2 transition-all duration-400 ${
                isDark 
                  ? 'bg-gradient-to-r from-[#1A1A1A] to-[#141414] border-[#D4AF37]/20 hover:border-[#D4AF37]/60 hover:shadow-[0_10px_40px_rgba(212,175,55,0.1)]' 
                  : 'bg-white border-[#D4AF37]/30 hover:border-[#D4AF37]/70 hover:shadow-[0_10px_40px_rgba(212,175,55,0.15)]'
              }`}
            >
              <div className={`flex items-center justify-center w-14 h-14 rounded-full transition-all duration-300 ${
                isDark 
                  ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/20' 
                  : 'bg-[#D4AF37]/15 border border-[#D4AF37]/40 group-hover:bg-[#D4AF37]/25'
              }`}>
                <Phone className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <span className={`block font-medium ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`}>
                  {t('contact.call')}
                </span>
                <span className={`text-sm ${isDark ? 'text-white/50' : 'text-[#1A1A1A]/50'}`}>
                  +33 6 19 36 25 20
                </span>
              </div>
            </a>

            {/* Email Button */}
            <a
              href="mailto:contact@hbtransfert.com"
              data-testid="contact-email-btn"
              className={`group flex items-center gap-4 p-5 rounded-sm border-2 transition-all duration-400 ${
                isDark 
                  ? 'bg-gradient-to-r from-[#1A1A1A] to-[#141414] border-[#D4AF37]/20 hover:border-[#D4AF37]/60 hover:shadow-[0_10px_40px_rgba(212,175,55,0.1)]' 
                  : 'bg-white border-[#D4AF37]/30 hover:border-[#D4AF37]/70 hover:shadow-[0_10px_40px_rgba(212,175,55,0.15)]'
              }`}
            >
              <div className={`flex items-center justify-center w-14 h-14 rounded-full transition-all duration-300 ${
                isDark 
                  ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/20' 
                  : 'bg-[#D4AF37]/15 border border-[#D4AF37]/40 group-hover:bg-[#D4AF37]/25'
              }`}>
                <Mail className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <span className={`block font-medium ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`}>
                  {t('contact.email')}
                </span>
                <span className={`text-sm ${isDark ? 'text-white/50' : 'text-[#1A1A1A]/50'}`}>
                  contact@hbtransfert.com
                </span>
              </div>
            </a>

            {/* WhatsApp Button */}
            <a
              href="https://wa.me/33619362520"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className={`group flex items-center gap-4 p-5 rounded-sm border-2 transition-all duration-400 ${
                isDark 
                  ? 'bg-gradient-to-r from-[#1A1A1A] to-[#141414] border-[#D4AF37]/20 hover:border-[#D4AF37]/60 hover:shadow-[0_10px_40px_rgba(212,175,55,0.1)]' 
                  : 'bg-white border-[#D4AF37]/30 hover:border-[#D4AF37]/70 hover:shadow-[0_10px_40px_rgba(212,175,55,0.15)]'
              }`}
            >
              <div className={`flex items-center justify-center w-14 h-14 rounded-full transition-all duration-300 ${
                isDark 
                  ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/20' 
                  : 'bg-[#D4AF37]/15 border border-[#D4AF37]/40 group-hover:bg-[#D4AF37]/25'
              }`}>
                <MessageCircle className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <span className={`block font-medium ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`}>
                  {t('contact.whatsapp')}
                </span>
                <span className={`text-sm ${isDark ? 'text-white/50' : 'text-[#1A1A1A]/50'}`}>
                  +33 6 19 36 25 20
                </span>
              </div>
            </a>
          </div>

          {/* Contact Form */}
          <div className={`p-8 md:p-10 rounded-sm border-2 ${
            isDark 
              ? 'bg-gradient-to-b from-[#1A1A1A] to-[#141414] border-[#D4AF37]/20' 
              : 'bg-white border-[#D4AF37]/30'
          }`}>
            <h3 
              data-testid="contact-form-title"
              className={`font-playfair text-xl md:text-2xl mb-8 ${
                isDark ? 'text-white' : 'text-[#1A1A1A]'
              }`}
            >
              {t('contact.form.title')}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-xs uppercase tracking-wider mb-2 ${
                  isDark ? 'text-white/60' : 'text-[#1A1A1A]/60'
                }`}>
                  {t('contact.form.name')}
                </label>
                <input
                  type="text"
                  name="name"
                  data-testid="contact-form-name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full bg-transparent border-b-2 py-3 outline-none transition-colors duration-300 ${
                    isDark 
                      ? 'border-white/10 text-white focus:border-[#D4AF37]' 
                      : 'border-[#1A1A1A]/10 text-[#1A1A1A] focus:border-[#D4AF37]'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block text-xs uppercase tracking-wider mb-2 ${
                  isDark ? 'text-white/60' : 'text-[#1A1A1A]/60'
                }`}>
                  {t('contact.form.emailField')}
                </label>
                <input
                  type="email"
                  name="email"
                  data-testid="contact-form-email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full bg-transparent border-b-2 py-3 outline-none transition-colors duration-300 ${
                    isDark 
                      ? 'border-white/10 text-white focus:border-[#D4AF37]' 
                      : 'border-[#1A1A1A]/10 text-[#1A1A1A] focus:border-[#D4AF37]'
                  }`}
                  required
                />
              </div>

              <div>
                <label className={`block text-xs uppercase tracking-wider mb-2 ${
                  isDark ? 'text-white/60' : 'text-[#1A1A1A]/60'
                }`}>
                  {t('contact.form.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  data-testid="contact-form-phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full bg-transparent border-b-2 py-3 outline-none transition-colors duration-300 ${
                    isDark 
                      ? 'border-white/10 text-white focus:border-[#D4AF37]' 
                      : 'border-[#1A1A1A]/10 text-[#1A1A1A] focus:border-[#D4AF37]'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-xs uppercase tracking-wider mb-2 ${
                  isDark ? 'text-white/60' : 'text-[#1A1A1A]/60'
                }`}>
                  {t('contact.form.message')}
                </label>
                <textarea
                  name="message"
                  data-testid="contact-form-message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className={`w-full bg-transparent border-b-2 py-3 outline-none transition-colors duration-300 resize-none ${
                    isDark 
                      ? 'border-white/10 text-white focus:border-[#D4AF37]' 
                      : 'border-[#1A1A1A]/10 text-[#1A1A1A] focus:border-[#D4AF37]'
                  }`}
                  required
                />
              </div>

              <button
                type="submit"
                data-testid="contact-form-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-8 py-4 text-sm font-semibold rounded-full hover:bg-[#F5E6A3] hover:shadow-[0_0_40px_rgba(212,175,55,0.5)] transition-all duration-400 mt-4"
              >
                <Send className="w-4 h-4" />
                {t('contact.form.submit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
